// Zoomable live spectrogram with playback for library calls (user 13 Oct 2026: "calls saved in a library should also
// behave like calls in the analysis so the user can choose detailed frequency, etc."). Same drawing, window rule,
// Detail menu and sound controls as the review; no drawing tools.
import React, { useEffect, useRef, useState } from 'react';
import { drawSpectrogram, play } from './liveSpec';
import DetailSelect from './DetailSelect';
import './CallReview.css';

const kHz = (f) => (f >= 1000 ? `${+(f / 1000).toFixed(f >= 10000 ? 0 : 1)} kHz` : `${Math.round(f)} Hz`);

/** sig = {x, fs, t0, t1}; call = {t0, t1, f0, f1} (file seconds / Hz); speed0 = default playback speed */
export default function LiveViewer({ sig, call, speed0 = 1 }) {
  const ext = { t0: sig.t0, t1: sig.t1, f1: sig.fs / 2 };
  const pad = call ? Math.max(0.1, 0.1 * (call.t1 - call.t0)) : 0;
  const startView = call ? { t0: Math.max(ext.t0, call.t0 - pad), t1: Math.min(ext.t1, call.t1 + pad), f0: 0,
    f1: Math.min(ext.f1, call.f1 && call.f1 * 2 < ext.f1 ? Math.max(call.f1 * 1.5, 200) : ext.f1) } : { t0: ext.t0, t1: ext.t1, f0: 0, f1: ext.f1 };
  const fullView = { t0: ext.t0, t1: ext.t1, f0: 0, f1: ext.f1 };
  const [view, setView] = useState(null);
  const [detail, setDetail] = useState(0);
  const [info, setInfo] = useState(null);
  const [tool, setTool] = useState('zoom');
  const [drag, setDrag] = useState(null);
  const [speed, setSpeed] = useState(speed0);
  const [loop, setLoop] = useState(false);
  const [bandOnly, setBandOnly] = useState(false);
  const [playing, setPlaying] = useState(null);
  const [pos, setPos] = useState(null);
  const canvas = useRef(null); const plot = useRef(null);
  const v = view || startView;
  const isFull = v.t0 <= ext.t0 + 1e-6 && v.t1 >= ext.t1 - 1e-6 && v.f0 <= 0 && v.f1 >= ext.f1 - 1e-6;
  const X = (t) => ((t - v.t0) / (v.t1 - v.t0)) * 100;
  const Y = (f) => (1 - (f - v.f0) / (v.f1 - v.f0)) * 100;
  const clamp = (q) => {
    const minT = Math.max(0.002, (ext.t1 - ext.t0) / 400); const minF = ext.f1 / 512;
    let { t0, t1, f0, f1 } = q;
    if (t1 - t0 < minT) { const m = (t0 + t1) / 2; t0 = m - minT / 2; t1 = m + minT / 2; }
    if (f1 - f0 < minF) { const m = (f0 + f1) / 2; f0 = m - minF / 2; f1 = m + minF / 2; }
    const wt = Math.min(t1 - t0, ext.t1 - ext.t0); const wf = Math.min(f1 - f0, ext.f1);
    t0 = Math.max(ext.t0, Math.min(t0, ext.t1 - wt)); f0 = Math.max(0, Math.min(f0, ext.f1 - wf));
    return { t0, t1: t0 + wt, f0, f1: f0 + wf };
  };
  const [width, setWidth] = useState(0);                 // redraw when the box changes size
  useEffect(() => {
    const el = plot.current; if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => setWidth(Math.round(el.clientWidth))); ro.observe(el); return () => ro.disconnect();
  }, []);
  const key = `${v.t0.toFixed(4)}|${v.t1.toFixed(4)}|${v.f0.toFixed(1)}|${v.f1.toFixed(1)}|${detail}|${width}`;
  useEffect(() => {
    const id = requestAnimationFrame(() => { try { setInfo(drawSpectrogram(canvas.current, sig, v, { minView: [startView, fullView], detail })); } catch (e) { /* drawn on the next change */ } });
    return () => cancelAnimationFrame(id);
  }, [key, sig]);
  useEffect(() => {
    if (!playing) return undefined;
    let id; const tick = () => { const p = playing.position(); if (p === null) { setPlaying(null); setPos(null); return; } setPos(p); id = requestAnimationFrame(tick); };
    id = requestAnimationFrame(tick); return () => cancelAnimationFrame(id);
  }, [playing]);
  useEffect(() => () => { if (playing) playing.stop(); }, [playing]);
  const vRef = useRef(v); vRef.current = v;
  useEffect(() => {                     // Ctrl / ⌘ + wheel zooms at the pointer
    const el = plot.current; if (!el) return undefined;
    const onWheel = (e) => {
      if (!(e.ctrlKey || e.metaKey)) return; e.preventDefault();
      const r = el.getBoundingClientRect(); const q = vRef.current;
      const ct = q.t0 + ((e.clientX - r.left) / r.width) * (q.t1 - q.t0); const cf = q.f0 + (1 - (e.clientY - r.top) / r.height) * (q.f1 - q.f0);
      const k = e.deltaY > 0 ? 1.25 : 0.8;
      setView(clamp({ t0: ct - (ct - q.t0) * k, t1: ct + (q.t1 - ct) * k, f0: cf - (cf - q.f0) * k, f1: cf + (q.f1 - cf) * k }));
    };
    el.addEventListener('wheel', onWheel, { passive: false }); return () => el.removeEventListener('wheel', onWheel);
  }, [sig]);
  const at = (e) => { const r = plot.current.getBoundingClientRect();
    const px = Math.max(0, Math.min(1, (e.clientX - r.left) / r.width)); const py = Math.max(0, Math.min(1, (e.clientY - r.top) / r.height));
    return { t: v.t0 + px * (v.t1 - v.t0), f: v.f0 + (1 - py) * (v.f1 - v.f0), r }; };
  const down = (e) => { const p = at(e); setDrag({ ...p, x: e.clientX, y: e.clientY, v0: v, t1: p.t, f1: p.f }); e.currentTarget.setPointerCapture?.(e.pointerId); };
  const move = (e) => {
    if (!drag) return; const p = at(e);
    if (tool === 'pan') { const dt = ((e.clientX - drag.x) / drag.r.width) * (drag.v0.t1 - drag.v0.t0); const df = ((e.clientY - drag.y) / drag.r.height) * (drag.v0.f1 - drag.v0.f0);
      setView(clamp({ t0: drag.v0.t0 - dt, t1: drag.v0.t1 - dt, f0: drag.v0.f0 + df, f1: drag.v0.f1 + df })); }
    else setDrag({ ...drag, t1: p.t, f1: p.f });
  };
  const up = () => {
    if (drag && tool === 'zoom') {
      const b = { t0: Math.min(drag.t, drag.t1), t1: Math.max(drag.t, drag.t1), f0: Math.min(drag.f, drag.f1), f1: Math.max(drag.f, drag.f1) };
      const wide = X(b.t1) - X(b.t0) >= 2; const tall = Y(b.f0) - Y(b.f1) >= 2;
      if (wide && tall) setView(clamp(b)); else if (wide) setView(clamp({ ...v, t0: b.t0, t1: b.t1 })); else if (tall) setView(clamp({ ...v, f0: b.f0, f1: b.f1 }));
    }
    setDrag(null);
  };
  const zoomBy = (k) => { const ct = (v.t0 + v.t1) / 2; const cf = (v.f0 + v.f1) / 2;
    setView(clamp({ t0: ct - (ct - v.t0) * k, t1: ct + (v.t1 - ct) * k, f0: cf - (cf - v.f0) * k, f1: cf + (v.f1 - cf) * k })); };
  const start = (a, b) => { if (playing) playing.stop(); setPlaying(play(sig, Math.max(sig.t0, a), Math.min(sig.t1, b), { speed, loop, band: bandOnly ? [v.f0, v.f1] : null })); };
  const stop = () => { if (playing) playing.stop(); setPlaying(null); setPos(null); };
  return (
    <div className="lv">
      <div className="cr-zoombar" role="toolbar" aria-label="Zoom">
        {['zoom', 'pan'].map((t) => <button key={t} type="button" className={`cr-tool ${tool === t ? 'on' : ''}`} aria-pressed={tool === t} onClick={() => setTool(t)}
          title={t === 'zoom' ? 'Drag a box to zoom into it (Ctrl / ⌘ + wheel zooms at the pointer)' : 'Drag to move the view'}>{t === 'zoom' ? '🔍 Zoom' : '✋ Move'}</button>)}
        <button type="button" className="ct-btn" aria-label="Zoom in" onClick={() => zoomBy(0.5)}>＋</button>
        <button type="button" className="ct-btn" aria-label="Zoom out" onClick={() => zoomBy(2)} disabled={isFull}>−</button>
        <button type="button" className="ct-btn" onClick={() => { setView(null); setDetail(0); }} disabled={!view && !detail} title="Back to the view the call opened with (and automatic detail)">↺ Default view</button>
        <button type="button" className="ct-btn" onClick={() => setView(fullView)} disabled={isFull}>Full range</button>
        <DetailSelect value={detail} onChange={setDetail} info={info} />
      </div>
      <div ref={plot} className={`lv-plot cr-tool-${tool}`} onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerLeave={up}>
        <canvas ref={canvas} className="cr-live" aria-label="Spectrogram of the library call" />
        {call && <div className="lv-call" style={{ left: `${X(call.t0)}%`, width: `${X(call.t1) - X(call.t0)}%` }} />}
        {drag && tool === 'zoom' && <div className="lv-box" style={{ left: `${Math.min(X(drag.t), X(drag.t1))}%`, width: `${Math.abs(X(drag.t1) - X(drag.t))}%`,
          top: `${Math.min(Y(drag.f), Y(drag.f1))}%`, height: `${Math.abs(Y(drag.f1) - Y(drag.f))}%` }} />}
        {pos !== null && pos >= v.t0 && pos <= v.t1 && <div className="cr-playhead" style={{ left: `${X(pos)}%` }} />}
        <span className="lv-ax lv-ax-top">{kHz(v.f1)}</span><span className="lv-ax lv-ax-bot">{kHz(v.f0)} · {(v.t1 - v.t0).toFixed(v.t1 - v.t0 < 1 ? 3 : 2)} s shown</span>
      </div>
      <div className="cr-soundbar" role="toolbar" aria-label="Sound">
        {playing ? <button type="button" className="ct-btn" onClick={stop}>■ Stop</button>
          : <button type="button" className="ct-btn green" onClick={() => start(v.t0, v.t1)}>▶ Play view</button>}
        {call && <button type="button" className="ct-btn" onClick={() => start(call.t0, call.t1)}>▶ Play call</button>}
        <label className="hcr-small">Speed <select className="hcr-input cr-speed" value={speed} onChange={(e) => setSpeed(Number(e.target.value))}>
          {[0.25, 0.5, 1, 2, 4, 10].map((k) => <option key={k} value={k}>{k === 1 ? 'normal' : `${k}×`}</option>)}</select></label>
        <label className="hcr-small ct-check"><input type="checkbox" checked={loop} onChange={(e) => setLoop(e.target.checked)} /> loop</label>
        <label className="hcr-small ct-check"><input type="checkbox" checked={bandOnly} onChange={(e) => setBandOnly(e.target.checked)} /> only the band in view</label>
      </div>
    </div>);
}
